import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    FaSearch,
    FaFilter,
    FaCalculator,
    FaMoneyBillWave,
    FaClock,
    FaExclamationTriangle,
    FaUsers,
    FaFileExcel,
    FaEllipsisV,
    FaCheckCircle,
    FaRegCircle,
    FaTimes,
    FaInfoCircle
} from "react-icons/fa";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Form, Button, Dropdown } from "react-bootstrap";
import { InputDataNativa } from "../input-data-nativa";
import { SubmenuAcaoLote } from "../listagem/submenu-acao-lote";
import Grid from "../grid";
import { AcaoGrid, FiltroDataPagamento, StatusPagamento } from "../../services/tipos";
import { converterFiltroDataPagamentoParaTexto, converterStatusPagamentoParaTexto, dateToInputValue, getWeekRange, inputValueToDate } from "../../services/utilitarios";
import { ajustarPeriodoPorNovaDataFinal, ajustarPeriodoPorNovaDataInicial } from "../../services/periodo";
import { useLocation, useHistory } from "react-router-dom";
import { obterIdTenent } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";

// Interfaces
export interface AcaoEmLote {
    titulo: string;
    icone: React.ElementType;
    onClick: (dataInicio: Date | null, dataFim: Date | null, status: StatusPagamento, itensSelecionados: (string | number)[]) => void;
    subAcoes?: AcaoEmLote[];
}

export interface OpcaoBusca {
    label: string;
    value: string | number;
}

// Interface para os Totalizadores
export interface Totalizadores {
    pagos: { valor: number; quantidade: number; };
    aVencer: { valor: number; quantidade: number; };
    emAtraso: { valor: number; quantidade: number; };
    locatarios: number;
}

interface ListagemPagamentoProps {
    buscar: boolean;
    carregando: boolean;
    linhas: GridRowsProp;
    nomeEntidade: string;
    colunas: GridColDef[];
    acoesExtras?: AcaoGrid[];
    exibirAcoesDeCrud?: boolean;
    IconeEntidade: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    onBuscar: (dataInicio: Date | null, dataFim: Date | null, status: StatusPagamento, buscarPendentesAteAgora?: boolean, itensSelecionados?: any, tipoPeriodo?: "vencimento" | "pagamento", statusLocacao?: "todas" | "ativas" | "encerradas") => void;
    filtrosIniciais?: {
        dataInicio?: Date | null;
        dataFim?: Date | null;
        status?: StatusPagamento;
        buscarPendentes?: boolean;
    };
    rowCount?: number;
    paginationModel?: { page: number; pageSize: number };
    onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
    acoesEmLote?: AcaoEmLote[];
    opcoesBusca?: OpcaoBusca[];
    placeholder?: string;
    dadosTotalizadores?: Totalizadores;
    carregandoResumo?: boolean;
    exportOptions?: {
        field: string;
        headerName: string;
        valueGetter?: (params: { row: any }) => any;
    }[];
    // Quando definido, a exportação chama esta função em vez do endpoint dedicado. Permite que o pai
    // (que conhece TODOS os filtros aplicados) garanta que o export reflete exatamente a listagem.
    onExportarBuscarTudo?: () => Promise<any[]>;
    tituloExportacao?: string;
    onNovo?: () => void;
}

// Botões Customizados
const CustomDropdownToggle = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }>(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
            style={{ height: "38px" }}
        >
            {children}
        </button>
    )
);

const SearchDropdownToggle = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }>(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
            className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-800 bg-white text-left flex justify-between items-center"
            style={{ minHeight: "34px" }}
        >
            {children}
        </button>
    )
);

// --- COMPONENTE ISOLADO PARA BUSCA ---
interface SearchableDropdownContentProps {
    opcoes: OpcaoBusca[];
    itensSelecionados: (string | number)[];
    onSelect: (val: string | number) => void;
    onClear: () => void;
}

const SearchableDropdownContent: React.FC<SearchableDropdownContentProps> = ({ 
    opcoes, 
    itensSelecionados, 
    onSelect, 
    onClear 
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [displayedOptions, setDisplayedOptions] = useState<OpcaoBusca[]>(opcoes);
    const [isFiltering, setIsFiltering] = useState(false);

    // Debounce da filtragem
    useEffect(() => {
        setIsFiltering(true);
        const handler = setTimeout(() => {
            if (!searchTerm) {
                setDisplayedOptions(opcoes);
            } else {
                const lowerTerm = searchTerm.toLowerCase();
                const filtered = opcoes.filter(op => 
                    op.label.toLowerCase().includes(lowerTerm)
                );
                setDisplayedOptions(filtered);
            }
            setIsFiltering(false);
        }, 300);

        return () => clearTimeout(handler);
    }, [searchTerm, opcoes]);

    return (
        <>
            <div className="p-2 border-b border-gray-100 bg-white sticky top-0 z-20">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-300 focus:shadow-outline-blue sm:text-sm transition duration-150 ease-in-out"
                        placeholder="Pesquisar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                </div>
            </div>

            {itensSelecionados.length > 0 && (
                <div
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 border-b border-gray-200 text-gray-600 bg-white sticky top-[53px] z-10"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                        setSearchTerm("");
                    }}
                >
                    <FaTimes className="w-3 h-3" />
                    <span className="text-sm font-medium">Limpar seleção</span>
                </div>
            )}

            <div className="max-h-60 overflow-y-auto">
                {isFiltering ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrando...
                    </div>
                ) : (
                    displayedOptions.length > 0 ? (
                        displayedOptions.map((opcao) => (
                            <div
                                key={opcao.value}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(opcao.value);
                                }}
                            >
                                <div className="text-blue-800">
                                    {itensSelecionados.includes(opcao.value) ? (
                                        <FaCheckCircle className="w-4 h-4" />
                                    ) : (
                                        <FaRegCircle className="w-4 h-4 text-gray-300" />
                                    )}
                                </div>
                                <span className="text-sm text-gray-700">{opcao.label}</span>
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            Nenhum resultado encontrado
                        </div>
                    )
                )}
            </div>
        </>
    );
};

// --- COMPONENTE PRINCIPAL ---

const ListagemPagamento: React.FC<ListagemPagamentoProps> = ({
    buscar,
    carregando,
    linhas,
    nomeEntidade,
    colunas,
    acoesExtras,
    exibirAcoesDeCrud,
    IconeEntidade,
    onBuscar,
    filtrosIniciais = {},
    rowCount,
    paginationModel,
    onPaginationModelChange,
    acoesEmLote,
    opcoesBusca = [],
    placeholder = "Selecione...",
    dadosTotalizadores,
    carregandoResumo,
    exportOptions,
    onExportarBuscarTudo,
    tituloExportacao,
    onNovo
}) => {
    const location = useLocation();
    const history = useHistory();
    const [showFilters, setShowFilters] = useState(true);
    const [showTotals, setShowTotals] = useState(false);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    
    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
    
    // Estados de Filtro
    const [dataInicio, setDataInicio] = useState<Date | null>(filtrosIniciais.dataInicio !== undefined ? filtrosIniciais.dataInicio : new Date());
    const [dataFim, setDataFim] = useState<Date | null>(filtrosIniciais.dataFim !== undefined ? filtrosIniciais.dataFim : new Date());
    const [status, setStatus] = useState<StatusPagamento>(filtrosIniciais.status || StatusPagamento.Todos);
    const [filtroData, setFiltroData] = useState<FiltroDataPagamento>(FiltroDataPagamento.Personalizado);
    const [itensSelecionados, setItensSelecionados] = useState<(string | number)[]>([]);
    const [tipoPeriodo, setTipoPeriodo] = useState<"vencimento" | "pagamento">("vencimento");
    const [statusLocacao, setStatusLocacao] = useState<"todas" | "ativas" | "encerradas">("todas");

    // Ref estável para onBuscar, evita re-disparar effects quando a referência muda
    const onBuscarRef = useRef(onBuscar);
    onBuscarRef.current = onBuscar;

    // filteredLinhas agora é apenas "linhas" que vem do pai paginado, mas mantemos o nome para compatibilidade se necessário,
    // embora a filtragem real seja no backend.
    const [filteredLinhas, setFilteredLinhas] = useState<GridRowsProp>(linhas);
    const [isSearching, setIsSearching] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [abrirModalExportacao, setAbrirModalExportacao] = useState(false);
    
    // Use os dados recebidos via prop ou um valor padrão zerado
    const totalizadores = dadosTotalizadores || {
        pagos: { valor: 0, quantidade: 0 },
        aVencer: { valor: 0, quantidade: 0 },
        emAtraso: { valor: 0, quantidade: 0 },
        locatarios: 0
    };

    const [token] = useState(useAppSelector(state => state.token));

    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [buscarPendentesAteAgora, setBuscarPendentesAteAgora] = useState(filtrosIniciais.buscarPendentes || false);

    // Efeito para sincronizar linhas quando a API responder
    useEffect(() => {
        setFilteredLinhas(linhas);
    }, [linhas]);

    // Processamento da URL
    useEffect(() => {
        const processUrlParams = () => {
            const searchParams = new URLSearchParams(location.search);
            let hasUrlFilters = false;

            const dataInicioParam = searchParams.get('dataInicio');
            const dataFimParam = searchParams.get('dataFim');

            let newDataInicio = dataInicio;
            let newDataFim = dataFim;
            let newStatus = status;

            if (dataInicioParam) {
                const date = new Date(dataInicioParam);
                if (!isNaN(date.getTime())) {
                    newDataInicio = date;
                    hasUrlFilters = true;
                }
            }

            if (dataFimParam) {
                const date = new Date(dataFimParam);
                if (!isNaN(date.getTime())) {
                    newDataFim = date;
                    hasUrlFilters = true;
                }
            }

            const statusParam = searchParams.get('status');
            if (statusParam) {
                const statusNum = parseInt(statusParam, 10);
                if (!isNaN(statusNum)) {
                    newStatus = statusNum as StatusPagamento;
                    hasUrlFilters = true;
                }
            }

            const pendentesParam = searchParams.get('Pendentes');
            const buscarPendentes = pendentesParam === 'true';

            if (hasUrlFilters || buscarPendentes) {
                setDataInicio(newDataInicio);
                setDataFim(newDataFim);
                setStatus(newStatus);
                history.replace(location.pathname);
                onBuscarRef.current(newDataInicio || new Date(), newDataFim || new Date(), newStatus, buscarPendentes, itensSelecionados, tipoPeriodo, statusLocacao);
            }

            setUrlParamsProcessed(true);
        };

        if (!urlParamsProcessed) {
            processUrlParams();
        }
    }, [location, history]);

    // Gatilho externo de busca
    useEffect(() => {
        if (urlParamsProcessed && buscar) {
            const searchParams = new URLSearchParams(location.search);
            const buscarPendentes = searchParams.get('Pendentes') === 'true';
            onBuscarRef.current(dataInicio || new Date(), dataFim || new Date(), status, buscarPendentes, itensSelecionados, tipoPeriodo, statusLocacao);
        }
    }, [buscar, urlParamsProcessed]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowFilters(true);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // OBS: O cálculo local de totalizadores foi removido pois agora os dados são paginados.
    // Os totais são recebidos via props (dadosTotalizadores).

    const { startOfWeek, endOfWeek } = getWeekRange(new Date());

    const executarBusca = () => {
        setIsSearching(true);
        onBuscar(dataInicio, dataFim, status, undefined, itensSelecionados, tipoPeriodo, statusLocacao);
        if (isMobile) {
            setShowFilters(false);
        }
    };

    useEffect(() => {
        if (!carregando) {
            setIsSearching(false);
        }
    }, [carregando]);

    const aplicarFiltroData = (filtro: FiltroDataPagamento) => {
        const hoje = new Date();
        let inicio: Date | null = null;
        let fim: Date | null = null;

        switch (filtro) {
            case FiltroDataPagamento.Hoje:
                inicio = hoje;
                fim = hoje;
                break;
            case FiltroDataPagamento.Semana:
                inicio = startOfWeek;
                fim = endOfWeek;
                break;
            case FiltroDataPagamento.Quinzena:
                inicio = new Date(hoje);
                inicio.setDate(hoje.getDate() - 7);
                fim = new Date(hoje);
                fim.setDate(hoje.getDate() + 7);
                break;
            case FiltroDataPagamento.Mes:
                inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
                break;
        }

        setDataInicio(inicio);
        setDataFim(fim);
        setFiltroData(filtro);
    };

    const handleStatusChange = (selected: string | null) => {
        if (selected !== null) {
            const statusSelecionado = parseInt(selected, 10);
            if (!isNaN(statusSelecionado)) {
                setStatus(statusSelecionado as StatusPagamento);
            }
        }
    };

    const handleFiltroDataChange = (selected: string | null) => {
        if (selected !== null) {
            const filtro = parseInt(selected, 10);
            if (!isNaN(filtro)) {
                aplicarFiltroData(filtro as FiltroDataPagamento);
            }
        }
    };

    const handleSearchSelect = useCallback((valor: string | number) => {
        setItensSelecionados(prev => {
            if (prev.includes(valor)) {
                return [];
            } else {
                setDataInicio(null);
                setDataFim(null);
                setFiltroData(FiltroDataPagamento.Todos); 
                return [valor];
            }
        });
    }, []);

    // Função que seleciona e fecha o dropdown (sem disparar busca automática)
    const handleSelectAndClose = (valor: string | number) => {
        handleSearchSelect(valor);
        setSearchDropdownOpen(false);
    };

    // Função que limpa e dispara a busca
    const handleClearSelection = useCallback(() => {
        setItensSelecionados([]);
        // Dispara busca imediatamente sem filtro de itens
        // Usa os estados atuais de data/status (que não mudam ao limpar, exceto se estavam null antes)
        // Assumindo que ao limpar volta a busca "geral" com os filtros que estiverem na tela.
        onBuscar(dataInicio, dataFim, status, undefined, [], tipoPeriodo, statusLocacao);
    }, [dataInicio, dataFim, status, onBuscar, tipoPeriodo, statusLocacao]);

    const handleDataInicioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ? new Date(e.target.value) : null;
        setDataInicio(value);
        
        if (value && dataFim && value > dataFim) {
            setDataFim(value);
        }

        setFiltroData(FiltroDataPagamento.Personalizado);
    };

    const handleDataFimChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ? new Date(e.target.value) : null;
        setDataFim(value);
        setFiltroData(FiltroDataPagamento.Personalizado);
    };

    const toggleFilters = () => {
        setShowFilters(prev => !prev);
    };

    const toggleTotals = () => {
        setShowTotals(prev => !prev);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const renderSelectLabel = () => {
        if (itensSelecionados.length === 0) return <span className="text-gray-400">{placeholder}</span>;
        if (itensSelecionados.length === 1) {
            const opcao = opcoesBusca.find(op => op.value === itensSelecionados[0]);
            return <span className="text-gray-800 text-truncate">{opcao ? opcao.label : itensSelecionados[0]}</span>;
        }
        return <span className="text-gray-800">{itensSelecionados.length} selecionado</span>;
    };

    const classeRotulo = "block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1";
    const classeCampoData = "flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800";
    const estiloCampoData = { height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" };

    return (
        <div className="flex flex-col h-[calc(100vh-128px)] w-full bg-white-100 overflow-hidden">
            <div className="flex-1 flex flex-col bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-3 border-b border-gray-200">
                    <div className="flex items-center mb-1 sm:mb-0">
                        <IconeEntidade className="text-blue-800 w-5 h-5 mr-2" />
                        <span className="text-base font-semibold text-gray-800">{nomeEntidade}</span>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                        {onNovo && (
                            <button
                                onClick={onNovo}
                                className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors bg-[#1F467E] text-white hover:bg-[#2a5a9e]"
                                style={{ height: "38px" }}
                            >
                                <span className="font-medium">+ Novo Pagamento</span>
                            </button>
                        )}
                        <button
                            onClick={() => setAbrirModalExportacao(true)}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaFileExcel className="w-3 h-3" />
                                <span className="font-medium">Exportar Dados</span>
                            </div>
                        </button>

                        {acoesEmLote && acoesEmLote.length > 0 && (
                            <Dropdown onToggle={(isOpen) => setActionsDropdownOpen(isOpen)}>
                                <Dropdown.Toggle as={CustomDropdownToggle}>
                                    <div className="flex items-center gap-2">
                                        <FaEllipsisV className="w-3 h-3" />
                                        <span className="font-medium">Ações em Lote</span>
                                        <svg
                                            className={`w-3 h-3 transform transition-transform ${actionsDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {acoesEmLote.map((acao, index) => {
                                        const IconeAcao = acao.icone;
                                        const executar = (a: AcaoEmLote) => a.onClick(dataInicio, dataFim, status, itensSelecionados);
                                        if (acao.subAcoes && acao.subAcoes.length > 0) {
                                            return <SubmenuAcaoLote key={index} acao={acao} executar={executar} />;
                                        }
                                        return (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() => executar(acao)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {IconeAcao && (
                                                        <span className="text-[#1F467E] flex items-center justify-center">
                                                            <IconeAcao className="w-4 h-4" />
                                                        </span>
                                                    )}
                                                    <span>{acao.titulo}</span>
                                                </div>
                                            </Dropdown.Item>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}

                        <button
                            onClick={toggleTotals}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaCalculator className="w-3 h-3" />
                                <span className="font-medium">Totalizadores</span>
                                <svg
                                    className={`w-3 h-3 transform transition-transform ${showTotals ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                        <button
                            onClick={toggleFilters}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaFilter className="w-3 h-3" />
                                <span className="font-medium">Filtros</span>
                                <svg
                                    className={`w-3 h-3 transform transition-transform ${showFilters ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>

                {showTotals && (
                    <div className="px-3 py-2 bg-white border-b border-gray-200">
                        <div className="mb-2 text-xs text-gray-600 flex items-center gap-1">
                            <FaInfoCircle className="text-[#1F467E]" />
                            <span>Os totalizadores consideram os filtros aplicados.</span>
                        </div>

                        {carregandoResumo ? (
                            <div className="text-center p-3 text-sm text-gray-500">Carregando resumo...</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {[
                                    { rotulo: "Recebidos", icone: FaMoneyBillWave, cor: "#2F855A", valor: formatCurrency(totalizadores.pagos.valor), detalhe: `${totalizadores.pagos.quantidade} ${totalizadores.pagos.quantidade === 1 ? 'item' : 'itens'}` },
                                    { rotulo: "A Vencer", icone: FaClock, cor: "#FFC107", valor: formatCurrency(totalizadores.aVencer.valor), detalhe: `${totalizadores.aVencer.quantidade} ${totalizadores.aVencer.quantidade === 1 ? 'item' : 'itens'}` },
                                    { rotulo: "Em Atraso", icone: FaExclamationTriangle, cor: "#C53030", valor: formatCurrency(totalizadores.emAtraso.valor), detalhe: `${totalizadores.emAtraso.quantidade} ${totalizadores.emAtraso.quantidade === 1 ? 'item' : 'itens'}` },
                                    { rotulo: "Locatários", icone: FaUsers, cor: "#1F467E", valor: String(totalizadores.locatarios), detalhe: totalizadores.locatarios === 1 ? 'locatário' : 'locatários' },
                                ].map((total) => {
                                    const Icone = total.icone;
                                    return (
                                        <div key={total.rotulo} className="bg-white rounded border border-gray-200 px-3 py-2" style={{ borderLeft: `4px solid ${total.cor}` }}>
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">{total.rotulo}</span>
                                                <Icone className="w-3.5 h-3.5 text-gray-400" />
                                            </div>
                                            <div className="text-lg font-semibold text-gray-900 leading-tight mt-1">{total.valor}</div>
                                            <div className="text-xs text-gray-500">{total.detalhe}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {showFilters && (
                    <div className="px-3 py-2 bg-white border-b border-gray-200">
                        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                            <div className="flex-[1.6] min-w-[220px]" data-tour="filtro-locacao">
                                <label className={classeRotulo}>Buscar</label>
                                <Dropdown className="w-full" show={searchDropdownOpen} onToggle={(isOpen) => setSearchDropdownOpen(isOpen)}>
                                    <Dropdown.Toggle as={SearchDropdownToggle}>
                                        <div className="flex-1 overflow-hidden">
                                            {renderSelectLabel()}
                                        </div>
                                        <svg className="w-4 h-4 text-gray-500 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full p-0" style={{ zIndex: 10001 }}>
                                        <SearchableDropdownContent
                                            opcoes={opcoesBusca}
                                            itensSelecionados={itensSelecionados}
                                            onSelect={handleSelectAndClose}
                                            onClear={handleClearSelection}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <div className="flex-[1.4] min-w-[340px] max-w-[460px]">
                                <div className="flex items-center justify-between mb-1">
                                    <label className={`${classeRotulo} mb-0`}>Período</label>
                                    <div className="flex rounded overflow-hidden border border-gray-300">
                                        <button type="button" onClick={() => setTipoPeriodo("vencimento")}
                                            className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${tipoPeriodo === "vencimento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                                            Vencimento
                                        </button>
                                        <button type="button" onClick={() => setTipoPeriodo("pagamento")}
                                            className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors border-l border-gray-300 ${tipoPeriodo === "pagamento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                                            Pagamento
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <InputDataNativa value={dateToInputValue(dataInicio)}
                                        onChange={(valor) => { const periodo = ajustarPeriodoPorNovaDataInicial(inputValueToDate(valor), dataFim); setDataInicio(periodo.dataInicio); setDataFim(periodo.dataFim); setFiltroData(FiltroDataPagamento.Personalizado); }}
                                        className={`form-control ${classeCampoData}`} style={estiloCampoData} />
                                    <span className="text-xs text-gray-500">até</span>
                                    <InputDataNativa value={dateToInputValue(dataFim)}
                                        onChange={(valor) => { const periodo = ajustarPeriodoPorNovaDataFinal(dataInicio, inputValueToDate(valor)); setDataInicio(periodo.dataInicio); setDataFim(periodo.dataFim); setFiltroData(FiltroDataPagamento.Personalizado); }}
                                        className={`form-control ${classeCampoData}`} style={estiloCampoData} />
                                </div>
                            </div>

                            <div className="flex-1 min-w-[150px]">
                                <label className={classeRotulo}>Atalho</label>
                                <Dropdown onSelect={handleFiltroDataChange}>
                                    <Dropdown.Toggle as={SearchDropdownToggle}>
                                        <span className="flex-1 text-left">{converterFiltroDataPagamentoParaTexto(filtroData)}</span>
                                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full">
                                        {Object.values(FiltroDataPagamento).map((filtroValue) =>
                                            typeof filtroValue === "number" && (
                                                <Dropdown.Item key={filtroValue} eventKey={filtroValue.toString()}>
                                                    {converterFiltroDataPagamentoParaTexto(filtroValue)}
                                                </Dropdown.Item>
                                            )
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <div className="flex-1 min-w-[150px]">
                                <label className={classeRotulo}>Status</label>
                                <Dropdown onSelect={handleStatusChange}>
                                    <Dropdown.Toggle as={SearchDropdownToggle}>
                                        <span className="flex-1 text-left">{converterStatusPagamentoParaTexto(status)}</span>
                                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full">
                                        {Object.values(StatusPagamento).map((statusValue) =>
                                            typeof statusValue === "number" && (
                                                <Dropdown.Item key={statusValue} eventKey={statusValue.toString()}>
                                                    {converterStatusPagamentoParaTexto(statusValue)}
                                                </Dropdown.Item>
                                            )
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <div className="flex-1 min-w-[170px]">
                                <label className={classeRotulo}>Status da Locação</label>
                                <Dropdown onSelect={(valor) => { if (valor) setStatusLocacao(valor as "todas" | "ativas" | "encerradas"); }}>
                                    <Dropdown.Toggle as={SearchDropdownToggle}>
                                        <span className="flex-1 text-left">
                                            {statusLocacao === "ativas" ? "Apenas Ativas" : statusLocacao === "encerradas" ? "Apenas Encerradas" : "Todas"}
                                        </span>
                                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full">
                                        <Dropdown.Item eventKey="todas">Todas</Dropdown.Item>
                                        <Dropdown.Item eventKey="ativas">Apenas Ativas</Dropdown.Item>
                                        <Dropdown.Item eventKey="encerradas">Apenas Encerradas</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <div data-tour="btn-buscar-pagamento">
                                <Button
                                    style={{
                                        backgroundColor: "#1F467E",
                                        color: "white",
                                        height: "34px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    className="text-sm px-4 rounded border-0 hover:bg-[#17355F]"
                                    onClick={executarBusca}
                                    disabled={isSearching}
                                >
                                    {isSearching ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Buscando...
                                        </span>
                                    ) : (
                                        <>
                                            Buscar <FaSearch className="ml-2 w-4 h-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 p-2 overflow-auto">
                    <Grid
                        tudoMaiusculo={true}
                        linhas={filteredLinhas}
                        colunas={colunas}
                        carregando={carregando || isSearching}
                        exibirAcoesDeCrud={exibirAcoesDeCrud || false}
                        acoesExtras={acoesExtras}
                        modalExportacaoAberto={abrirModalExportacao}
                        abrirModalExportacao={() => setAbrirModalExportacao(true)}
                        fecharModalExportacao={() => setAbrirModalExportacao(false)}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={onPaginationModelChange}
                        // Quando o pai fornecer `onExportarBuscarTudo`, esse callback é usado e
                        // garante alinhamento total com os filtros da listagem. Caso contrário, mantém
                        // o comportamento legado via endpoint dedicado.
                        {...(onExportarBuscarTudo
                            ? { buscarTudoParaExportar: onExportarBuscarTudo }
                            : {
                                endpointExport: "/Pagamento/Exportar",
                                getFiltrosParaExportar: () => ({
                                    idEmpresa: obterIdTenent(token),
                                    dataInicial: dataInicio,
                                    dataFim: dataFim,
                                    status: status,
                                    idLocacao: itensSelecionados.length > 0 ? itensSelecionados[0].toString() : null,
                                    tipoPeriodo: tipoPeriodo === "pagamento" ? "pagamento" : undefined,
                                    statusLocacao: statusLocacao === "ativas" ? 1 : statusLocacao === "encerradas" ? 3 : undefined
                                })
                            })}
                        tituloExportacao={tituloExportacao || nomeEntidade}
                        exportOptions={exportOptions}
                    />
                </div>
            </div>
        </div>
    );
};

export default ListagemPagamento;